import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { apiService } from '../services/api';
import { MagicBackground } from '../components/MagicBackground';

export default function QueueAuth() {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    loadVendor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const loadVendor = async () => {
    if (!vendorId) {
      setError('Invalid vendor ID');
      setChecking(false);
      return;
    }

    try {
      // Try to get vendor info (this will work if authenticated, otherwise we'll handle PIN)
      // For now, we'll just check if vendor exists via a public endpoint or store PIN in session
      setChecking(false);
    } catch (error) {
      setChecking(false);
      // Vendor check failed, but we can still proceed with PIN entry
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!pin || pin.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }

    if (!vendorId) {
      setError('Invalid vendor ID');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Verify PIN via API service
      const result = await apiService.verifyQueueAccess(vendorId, pin);
      
      if (result.verified) {
        // Store authenticated state
        sessionStorage.setItem(`queue_auth_${vendorId}`, 'true');
        sessionStorage.setItem(`queue_pin_${vendorId}`, pin);
        if (result.vendor) {
          sessionStorage.setItem(`queue_vendor_${vendorId}`, JSON.stringify({
            vendorId: result.vendor.vendorId,
            businessName: result.vendor.businessName,
            businessType: result.vendor.businessType || 'order',
            verified: true
          }));
        }
        
        // Navigate to queue
        navigate(`/queue/${vendorId}`);
      } else {
        setError('Invalid PIN. Please try again.');
      }
    } catch (error: any) {
      console.error('Error verifying PIN:', error);
      setError(error.response?.data?.error?.message || error.message || 'Failed to verify PIN. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        }}
      >
        <MagicBackground />
        <CircularProgress sx={{ color: '#667eea', position: 'relative', zIndex: 1 }} size={60} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        minHeight: '100vh',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <MagicBackground />
      
      <Card
        sx={{
          maxWidth: 400,
          width: '100%',
          mx: 3,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 3,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <LockIcon sx={{ fontSize: 48, color: '#667eea', mb: 2 }} />
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Queue Access
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.7)" textAlign="center">
              Enter your PIN to access the queue
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(211, 47, 47, 0.1)', color: 'white' }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="PIN"
              value={pin}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setPin(value);
                setError('');
              }}
              type="password"
              placeholder="Enter 4-6 digit PIN"
              autoFocus
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !pin}
              sx={{
                bgcolor: '#667eea',
                py: 1.5,
                '&:hover': { bgcolor: '#5568d3' },
                '&:disabled': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Access Queue'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}

